import { useMemo } from "react";
import { Link, useNavigate } from "react-router";

import { getComboTitle } from "@datanac/datanac-api-toolkit/dist/insurance/optimizer/optimizer";
import { Box, Chip, Stack, Typography, useMediaQuery } from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid";
import TilleyDataGrid from "components/shared/DataGrid/TilleyDataGrid";
import { formatNumber, formatPracticeName, formatPrice } from "utility/formatters";

import { NavigateIcon } from "../../Icons/Icons";

export default function InsuranceCountyPracticeDataTable({
  insuranceScenario,
  isLoading,
  isFilter,
  pageSizes = [5, 10, 25, 50, 100],
  rowHeight = 80,
}) {
  const navigate = useNavigate();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("lg"), { noSsr: true });

  const handleCardNavigateClick = (currentCounty) => {
    if (insuranceScenario?.id && currentCounty.state_name) {
      navigate(
        `/insurance/${insuranceScenario?.id}/${currentCounty.state_name}/${currentCounty.county_name}/${currentCounty.commodity_name}/${currentCounty.type_name}/${currentCounty.practice_name}`
      );
    }
  };

  const filteredScenario = () => {
    if (isFilter) {
      return (
        insuranceScenario?.totals?.counties?.flatMap((currentCounty) => {
          const _countyScenarios = insuranceScenario?.budgetCountyPractices?.filter(
            (ins) => ins.state_name === currentCounty.state_name && ins.county_name === currentCounty.county_name
          );

          return _countyScenarios.map((practice) => ({
            ...practice,
          }));
        }) || []
      );
    }

    return insuranceScenario?.budgetCountyPractices || [];
  };

  const columns = useMemo(
    () => [
      {
        field: "county_name",
        headerName: "County",
        flex: 0.8,
        renderCell: (params) => (
          <Typography
            color="primary"
            component={Link}
            to={`/insurance/${insuranceScenario?.id}/${params.row.state_name}/${params.row.county_name}/${params.row.commodity_name}/${params.row.type_name}/${params.row.practice_name}`}
          >
            {params.row.county_name}
          </Typography>
        ),
      },
      {
        field: "state_name",
        headerName: "State",
        flex: 0.8,
      },
      {
        field: "commodity_name",
        headerName: "Commodity",
        flex: 0.8,
      },
      {
        field: "practice_name",
        headerName: "Practice",
        flex: 0.8,
        renderCell: (params) => {
          return formatPracticeName(params.row?.practice_name);
        },
      },
      {
        field: "producer_premium_amount_per_acre",
        headerName: "Producer",
        flex: 0.8,
        renderCell: (params) => {
          return formatPrice(params.row.totals?.producer_premium_amount_per_acre, 2);
        },
      },
      {
        field: "liability_amount_per_acre",
        headerName: "Coverage",
        flex: 0.8,
        renderCell: (params) => {
          return formatPrice(params.row.totals?.liability_amount_per_acre, 0);
        },
      },
      {
        field: "insurance_plans",
        headerName: "Plans",
        flex: 2,
        display: "flex",
        renderCell: (params) => {
          return (
            <Box
              className="crop-profile-data-grid"
              sx={{
                width: "100%",
              }}
            >
              <Stack direction="row" spacing={0} sx={{ flexWrap: "wrap", rowGap: 0, columnGap: 1 }}>
                {params.row?.insurancePlans?.map((currentInsurancePlan, index) => (
                  <Chip
                    key={index}
                    label={getComboTitle([currentInsurancePlan])}
                    sx={{
                      textOverflow: "ellipsis",
                      textTransform: "uppercase",
                      background: "transparent",
                      color: "text.secondary",
                      fontWeight: 600,
                      fontSize: "14px",
                    }}
                  />
                ))}
              </Stack>
            </Box>
          );
        },
      },
      {
        field: "actions",
        headerName: "Actions",
        type: "actions",
        headerAlign: "right",
        align: "right",
        width: 100,
        sortable: false,
        getActions: (params) => {
          return [
            <GridActionsCellItem
              key="view"
              icon={<NavigateIcon />}
              label="View Details"
              onClick={() => handleCardNavigateClick(params.row)}
            />,
          ];
        },
        flex: 0,
      },
    ],
    [insuranceScenario, getComboTitle, formatNumber, formatPracticeName, formatPrice]
  );

  return (
    <>
      <TilleyDataGrid
        rows={filteredScenario()}
        columns={columns}
        pageSizeOptions={pageSizes}
        defaultPageSize={isMobile ? 5 : 10}
        gridKey="insurance-county-practice"
        getRowId={(row) => row.id}
        rowHeight={rowHeight}
        isLoading={isLoading}
        sx={{ width: "99%", boxSizing: "border-box" }}
      />
    </>
  );
}
